import { Component, Input, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subject, from } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';
import { ScheduleEnum, ScheduleType } from 'src/app/product-capture/model/enums/product-capture.enums';
import { SCHEDULE_TYPE_ENUM } from 'src/app/product-capture/model/enums/schedule-type.enum';
import { ScheduleFormUtils } from 'src/app/product-capture/utils/schedule.utils';
import { ModalService } from 'src/app/ui-kit/dc-modal/modal.service';
import { DateUtils } from 'src/app/utils/date.utils';

export enum IrregularPeriodModeTypes {
  Day = 'day',
  Period = 'period',
}

export const RollDayRows = {
  [SCHEDULE_TYPE_ENUM.ACCRUAL_PRIMARY]: 'Accrual dates',
  [SCHEDULE_TYPE_ENUM.PAYMENT_PRIMARY]: 'Payment dates',
  [SCHEDULE_TYPE_ENUM.PAYMENT_ONLY]: 'Payment dates',
  [SCHEDULE_TYPE_ENUM.OBS_PRIMARY]: 'Observation dates',
}

@Component({
  selector: 'app-roll-days-modal',
  templateUrl: './roll-days-modal.component.html',
  styleUrls: ['./roll-days-modal.component.scss']
})
export class RollDaysModalComponent {

  irregularPeriodMode = IrregularPeriodModeTypes.Day;

  IrregularPeriodModeTypes = IrregularPeriodModeTypes;

  @Input()
  form: FormGroup;

  @Input()
  scheduleKey: ScheduleType;

  modalRef: NgbModalRef;

  @ViewChild('templateRef', { static: true }) 
  template: TemplateRef<any>;

  destroy$ = new Subject<void>();

  rows = [];

  constructor(private modalService: ModalService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.scheduleKey === ScheduleType.PARTICIPATION) {
      return;
    }

    if (changes.form && changes.form.currentValue) {
      this.destroy$.next();

      this.scheduleType.valueChanges
        .pipe(
          takeUntil(this.destroy$),
          startWith(this.scheduleTypeValue)
        ).subscribe(() => {
          this.resetRollConfigs();
          if (this.scheduleTypeValue && this.scheduleTypeValue !== SCHEDULE_TYPE_ENUM.INDEPENDENT) {
            this.clearAndSetRollConfigs();
          }
        });
    }
  }

  openModal(): Observable<any> {
    this.setupMode();
    this.setupRows();
    this.convertPeriods(true);

    this.modalRef = this.modalService.openModal({
      size: 'md-extended',
      backdrop: 'static',
    }, this.template);


    return from(this.modalRef.result);
  }

  convertPeriods(opening: boolean): void {
    if (this.irregularPeriodMode === IrregularPeriodModeTypes.Period) {
      const { obsRollConfig, pmtRollConfig, accrualRollConfig } = this.form.controls as any;
      this.convertPeriodInForm(obsRollConfig, opening);
      this.convertPeriodInForm(pmtRollConfig, opening);
      this.convertPeriodInForm(accrualRollConfig, opening);
    }
  }

  convertPeriodInForm(rollDayConfigForm: FormGroup, opening: boolean): void {
    if (!rollDayConfigForm) {
      return;
    }

    const { endFirstPeriod, startLastPeriod } = rollDayConfigForm.value;

    const convertFn = opening ? DateUtils.convertDateToNgbDate : DateUtils.convertNgbDateToDate;

    let convertedEndFirstPeriod: any = convertFn(endFirstPeriod);
    let convertedStartLastPeriod: any = convertFn(startLastPeriod);

    // This is how the Scheduler expects the dates
    if (!opening) {
      if (convertedEndFirstPeriod) {
        convertedEndFirstPeriod = DateUtils.formatDate(convertedEndFirstPeriod as Date, 'YYYY-MM-DD');
      }

      if (convertedStartLastPeriod) {
        convertedStartLastPeriod = DateUtils.formatDate(convertedStartLastPeriod as Date, 'YYYY-MM-DD');
      }
    }

    rollDayConfigForm.patchValue({
      endFirstPeriod: convertedEndFirstPeriod,
      startLastPeriod: convertedStartLastPeriod,
    }, { emitEvent: false });
  }

  setupMode(): void {
    const rollDayConfigForm = this.form.get(this.getFormName(this.scheduleTypeValue));
    if (rollDayConfigForm) {
      const { endFirstPeriod, startLastPeriod } = rollDayConfigForm.value;
      if (endFirstPeriod || startLastPeriod) {
        this.irregularPeriodMode = IrregularPeriodModeTypes.Period;
      }
    }
  }

  setupRows() {
    if (this.scheduleTypeValue === SCHEDULE_TYPE_ENUM.INDEPENDENT) {
      this.rows = [
        SCHEDULE_TYPE_ENUM.PAYMENT_PRIMARY, 
        SCHEDULE_TYPE_ENUM.OBS_PRIMARY, 
        SCHEDULE_TYPE_ENUM.ACCRUAL_PRIMARY
      ];
    } else {
      this.rows = [this.scheduleTypeValue];
    }
  }

  resetRollConfigs(): void {
    this.form.addControl(ScheduleEnum.obsRollConfig, ScheduleFormUtils.buildRollConfigForm());
    this.form.addControl(ScheduleEnum.pmtRollConfig, ScheduleFormUtils.buildRollConfigForm());
    this.form.addControl(ScheduleEnum.accrualRollConfig, ScheduleFormUtils.buildRollConfigForm());
  }

  clearAndSetRollConfigs(): void {
    const formName = this.getFormName(this.scheduleTypeValue);
    Object.keys(this.form.controls).filter(key => key.indexOf('RollConfig') > -1 && key !== formName)
        .forEach(key => this.form.removeControl(key));

    if (!this.form.get(formName)) {
      this.form.addControl(formName, ScheduleFormUtils.buildRollConfigForm());
    }
  }

  getFormName(scheduleType: SCHEDULE_TYPE_ENUM): string {
    switch (scheduleType) {
      case SCHEDULE_TYPE_ENUM.PAYMENT_PRIMARY:
      case SCHEDULE_TYPE_ENUM.PAYMENT_ONLY:
        return 'pmtRollConfig';
      case SCHEDULE_TYPE_ENUM.OBS_PRIMARY:
      case SCHEDULE_TYPE_ENUM.OBS_ONLY:
        return 'obsRollConfig';
      case SCHEDULE_TYPE_ENUM.ACCRUAL_PRIMARY:
        return 'accrualRollConfig';
      default:
        return null;
    }
  }

  onSelectMode(mode: IrregularPeriodModeTypes) {
    if (!mode) {
      return;
    }
    
    if (this.irregularPeriodMode) {
      this.form.get(ScheduleEnum.obsRollConfig)?.reset();
      this.form.get(ScheduleEnum.pmtRollConfig)?.reset();
      this.form.get(ScheduleEnum.accrualRollConfig)?.reset();
    }

    this.irregularPeriodMode = mode;
  }
  
  getRowLabel(row: string): string {
    return RollDayRows[row];
  }

  get scheduleType(): FormControl {
    return this.form.get(ScheduleEnum.scheduleType) as FormControl;
  }

  get scheduleTypeValue(): SCHEDULE_TYPE_ENUM {
    return this.scheduleType.value;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}
